/**
 * Platform Initialization
 * Initializes all analytics platforms after CDN scripts are loaded
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { PlatformReinitializer } from '../../core/platform-reinit';
import { isBot } from '../utils';
import { DEFAULT_CMP_TIMEOUT_MS } from '../../consent/consent-manager';
import { runWithDedupedListeners } from '../../core/listener-dedupe';

/**
 * Call appStartSection1 with exponential backoff retry.
 * Matches the extension's behavior of retrying if the function isn't available yet.
 * Extension uses 10 retries with 500ms initial delay.
 */
function callAppStartSection1WithRetry(analyzify: WindowAnalyzify, attempt = 1, maxAttempts = 10): void {
  if (typeof analyzify.appStartSection1 === 'function') {
    analyzify.appStartSection1();
    analyzify.log(`appStartSection1 called successfully (attempt ${attempt})`, 'dispatcher');

    // Set app_start flag after successful initialization (matches extension behavior)
    analyzify.initial_load.app_start = true;
    return;
  }

  if (attempt >= maxAttempts) {
    analyzify.log(
      'appStartSection1 not available after max retries - variant tracking may not work',
      'dispatcher'
    );
    return;
  }

  // Exponential backoff: 500ms, 1000ms, 2000ms... (matches extension)
  const delay = 500 * Math.pow(2, attempt - 1);
  setTimeout(() => {
    callAppStartSection1WithRetry(analyzify, attempt + 1, maxAttempts);
  }, delay);
}

/**
 * CRITICAL: Ensure collection data is available in ALL places CDN reads from.
 * CDN scripts read from: window.analyzify.collection, window.analyzify.getCollectionObj, window.collection
 */
function ensureCollectionProducts(analyzify: WindowAnalyzify): void {
  const win = window as any;
  const winAnalyzify = win.analyzify;

  if (!winAnalyzify) {
    analyzify.log('window.analyzify not found in ensureCollectionProducts', 'dispatcher');
    return;
  }

  const hasWindowCollection = win.collection && Array.isArray(win.collection.products);
  const products = hasWindowCollection ? win.collection.products : [];
  const collectionId = win.collection?.id ?? 0;
  const collectionTitle = win.collection?.title ?? '';
  const collectionHandle = win.collection?.handle ?? '';

  // 1. Ensure window.analyzify.collection has products
  winAnalyzify.collection = {
    id: collectionId,
    title: collectionTitle,
    handle: collectionHandle,
    products: products,
    all_products_count: products.length,
    products_count: products.length,
  };

  // 2. CRITICAL: Ensure window.analyzify.getCollectionObj has products
  // CDN's gtmViewItemList reads from getCollectionObj.products directly!
  if (!winAnalyzify.getCollectionObj) {
    winAnalyzify.getCollectionObj = {};
  }
  winAnalyzify.getCollectionObj.products = products;
  winAnalyzify.getCollectionObj.id = collectionId;
  winAnalyzify.getCollectionObj.title = collectionTitle;
  winAnalyzify.getCollectionObj.handle = collectionHandle;
  winAnalyzify.getCollectionObj.collection = winAnalyzify.collection;
  winAnalyzify.getCollectionObj.collection_sku_list = products.map((p: any) => p.variants?.[0]?.sku || '').filter(Boolean);

  // 3. Ensure window.collection exists
  if (!win.collection) {
    win.collection = winAnalyzify.collection;
  }

  // 4. Sync analyzify for SDK-side consistency
  (analyzify as any).collection = winAnalyzify.collection;
  (analyzify as any).getCollectionObj = winAnalyzify.getCollectionObj;

  if (hasWindowCollection) {
    analyzify.log(`Collection synced: ${products.length} products`, 'dispatcher');
  }
}

/**
 * Steps 1–3 of platform init: variant tracking setup, platform init calls,
 * third-party integrations (all synchronous CDN calls).
 */
function initPlatforms(analyzify: WindowAnalyzify): void {
  const props = analyzify.properties;

  // 1. Call appStartSection1 FIRST for variant tracking setup on product pages
  callAppStartSection1WithRetry(analyzify);

  // 2. Initialize platforms

  // Initialize GA4/GADS
  if (props.GA4?.status || props.GADS?.status) {
    if (typeof analyzify.initGa4Gads === 'function') {
      analyzify.initGa4Gads(analyzify, props.GA4, props.GADS);
    }
  }

  // Re-ensure collection after GA4/GADS (CDN may have overwritten it)
  ensureCollectionProducts(analyzify);

  // Initialize Facebook
  if (props.FACEBOOK?.status) {
    if (typeof analyzify.initFacebook === 'function') {
      analyzify.initFacebook(analyzify, props.FACEBOOK);
    }
  }

  // Re-ensure collection before GTM (GTM reads collection.products)
  ensureCollectionProducts(analyzify);

  // Initialize GTM
  if (props.GTM?.status) {
    if (typeof analyzify.initGTM === 'function') {
      analyzify.initGTM(analyzify, props.GTM);
    }
  }

  // Initialize TikTok (skip for bots to prevent polluting analytics)
  if (props.TIKTOK?.status && !isBot()) {
    if (typeof analyzify.initTiktok === 'function') {
      analyzify.initTiktok(analyzify, props.TIKTOK);
      analyzify.log('TikTok initialized', 'dispatcher');
    }
  } else if (props.TIKTOK?.status && isBot()) {
    analyzify.log('TikTok initialization skipped - bot detected', 'dispatcher');
  }

  // Initialize Pinterest
  if (props.PINTEREST?.status) {
    if (typeof analyzify.initPinterest === 'function') {
      analyzify.initPinterest(analyzify, props.PINTEREST);
    }
  }

  // Re-ensure collection before Bing (Bing reads collection.products)
  ensureCollectionProducts(analyzify);

  // Initialize Bing
  if (props.BING?.status) {
    if (typeof analyzify.initBing === 'function') {
      analyzify.initBing(analyzify, props.BING);
    }
  }

  // Initialize Snapchat
  if (props.SNAPCHAT?.status) {
    if (typeof analyzify.initSnapchat === 'function') {
      analyzify.initSnapchat(analyzify, props.SNAPCHAT);
    }
  }

  // Initialize Klaviyo
  // CRITICAL: Do NOT call initKlaviyo here!
  // Klaviyo CDN captures `collection` variable in closure ONLY when shopify_template === 'collection'.
  // If we call initKlaviyo here (before registerAllFunctions sets template to 'collection'),
  // the CDN defines klaviyoAddToCart WITHOUT the collection variable, causing ReferenceError later.
  // Instead, registerAllFunctions() handles Klaviyo init with proper collection context.

  // Initialize Criteo
  if (props.CRITEO?.status) {
    if (typeof analyzify.initCriteo === 'function') {
      analyzify.initCriteo(analyzify, props.CRITEO);
    }
  }

  // Initialize X (Twitter)
  if (props.X?.status) {
    if (typeof analyzify.initX === 'function') {
      analyzify.initX(analyzify, props.X);
    }
  }

  // Initialize Clarity
  if (props.CLARITY?.status) {
    if (typeof analyzify.initClarity === 'function') {
      analyzify.initClarity(props.CLARITY);
    }
  }

  // Initialize Hotjar
  if (props.HOTJAR?.status) {
    if (typeof analyzify.initHotjar === 'function') {
      analyzify.initHotjar(props.HOTJAR);
    }
  }

  // Initialize Server-Side
  if (props.SERVERSIDE?.status) {
    if (typeof analyzify.initGTAG_SS === 'function') {
      analyzify.initGTAG_SS(props.SERVERSIDE);
    }
  }

  // 3. Initialize third-party integrations

  // Initialize Shogun if active
  if (analyzify.shogun_active && typeof analyzify.initShogun === 'function') {
    analyzify.initShogun();
    analyzify.log('Shogun initialized', 'dispatcher');
  }

  // Initialize Rebuy if active
  if (analyzify.rebuy_atc_rfc && typeof analyzify.initRebuy === 'function') {
    analyzify.initRebuy();
    analyzify.log('Rebuy initialized', 'dispatcher');
  }
}

/**
 * Attempt platform initialization.
 * Called after CDN scripts are loaded.
 *
 * Order of operations matches extension:
 * 1. appStartSection1 - Sets up variant tracking event listeners
 * 2. Platform initialization - GA4, Facebook, etc.
 * 3. Third-party integrations - Shogun, Rebuy
 * 4. Consent processing
 */
export async function attemptPlatformInit(
  analyzify: WindowAnalyzify,
  reinitializer: PlatformReinitializer
): Promise<void> {
  const props = analyzify.properties;

  // Initial setup
  ensureCollectionProducts(analyzify);

  // Platform init re-adds the CDN listeners the function registration already
  // installed (form lead tracking etc.); keep one copy of each.
  runWithDedupedListeners(() => initPlatforms(analyzify));

  // 4. Process consent queue if consent is active
  if (analyzify.consent_active && analyzify.consentManager) {
    if (analyzify.consentManager.hasCMP) {
      // CMP is configured (e.g. a cookie-consent platform) — do NOT force-grant consent.
      const waitMs = (analyzify.consentManager as any).cmpTimeout || DEFAULT_CMP_TIMEOUT_MS;
      setTimeout(() => {
        if (!analyzify.consentManager?.consentReady) {
          const fallbackMode: 'granted' | 'denied' =
            (analyzify.consentManager as any).timeoutFallback === 'deny' ? 'denied' : 'granted';
          analyzify.log(
            `CMP timeout — ${fallbackMode === 'denied' ? 'denying' : 'granting'} consent as fallback`,
            'dispatcher'
          );
          const fallbackConsent = {
            ad_storage: fallbackMode,
            analytics_storage: fallbackMode,
            ad_user_data: fallbackMode,
            ad_personalization: fallbackMode,
            personalization_storage: fallbackMode,
            functionality_storage: 'granted' as const,
            security_storage: 'granted' as const,
            sale_of_data: fallbackMode,
          };
          analyzify.current_consent = {
            ...analyzify.current_consent,
            ...fallbackConsent,
          };
          // Keep the SDK's own consent state (canTrack, dataLayer consentSettings,
          // event buffer) in line with what the CDN scripts are told.
          (analyzify.consentManager as any).applySdkFallback?.(fallbackMode === 'granted');
          analyzify.consentManager!.consentReady = true;
          analyzify.consentManager!.processConsentQueue();
          analyzify.consentManager!.dispatchUpdate?.(analyzify.current_consent, 'fallback');
        }
      }, waitMs);
      analyzify.log(`CMP detected — waiting ${waitMs}ms for CMP consent events`, 'dispatcher');
    } else {
      // No CMP configured — apply the configured fallback (default: grant).
      const fallbackMode: 'granted' | 'denied' =
        (analyzify.consentManager as any).timeoutFallback === 'deny' ? 'denied' : 'granted';
      const defaultConsent: {
        ad_storage: 'granted' | 'denied';
        analytics_storage: 'granted' | 'denied';
        ad_user_data: 'granted' | 'denied';
        ad_personalization: 'granted' | 'denied';
        personalization_storage: 'granted' | 'denied';
        functionality_storage: 'granted';
        security_storage: 'granted';
        sale_of_data: 'granted' | 'denied';
      } = {
        ad_storage: fallbackMode,
        analytics_storage: fallbackMode,
        ad_user_data: fallbackMode,
        ad_personalization: fallbackMode,
        personalization_storage: fallbackMode,
        functionality_storage: 'granted',
        security_storage: 'granted',
        sale_of_data: fallbackMode,
      };

      analyzify.current_consent = {
        ...analyzify.current_consent,
        ...defaultConsent,
      };
      (analyzify.consentManager as any).applySdkFallback?.(fallbackMode === 'granted');

      analyzify.consentManager.processConsentQueue();
      analyzify.consentManager.dispatchUpdate?.(analyzify.current_consent, 'default');
      analyzify.log(`No CMP — consent ${fallbackMode === 'denied' ? 'denied' : 'granted'} by default`, 'dispatcher');
    }
  }

  // 5. Call appStart() to signal all platforms are ready
  if (typeof analyzify.appStart === 'function') {
    analyzify.appStart();
    analyzify.log('appStart called', 'dispatcher');
  }

  analyzify.log('Platform initialization complete', 'dispatcher');

  // 6. Register all page-specific functions for SPA navigation
  try {
    await reinitializer.registerAllFunctions();
  } catch (error) {
    analyzify.log(`Function registration failed: ${error}`, 'dispatcher');
  }

  // 7. CRITICAL: Re-ensure collection products AFTER registerAllFunctions
  ensureCollectionProducts(analyzify);
}
